<template>
	<div
		class="card-component bg-white rounded-lg transition-all duration-200"
		:class="[
			shadowClass,
			hoverClass,
			clickable ? 'cursor-pointer' : '',
			bordered ? 'border border-border' : '',
			customClass,
		]"
		@click="handleClick"
	>
		<!-- Card Header -->
		<div
			v-if="$slots.header || title"
			class="card-header px-6 py-4"
			:class="[
				headerClass,
				!noDivider && ($slots.default || $slots.body)
					? 'border-b border-border'
					: '',
			]"
		>
			<slot name="header">
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-3">
						<div
							v-if="icon"
							class="flex items-center justify-center w-10 h-10 rounded-lg"
							:class="iconBgClass"
						>
							<i
								:class="icon"
								class="text-lg"
								:style="{ color: iconColor }"
							></i>
						</div>
						<div>
							<h3 class="text-lg font-semibold text-text-primary">
								{{ title }}
							</h3>
							<p
								v-if="subtitle"
								class="text-sm text-text-secondary mt-0.5"
							>
								{{ subtitle }}
							</p>
						</div>
					</div>
					<slot name="header-actions"></slot>
				</div>
			</slot>
		</div>

		<!-- Card Body -->
		<div
			v-if="$slots.default || $slots.body"
			class="card-body"
			:class="[padding ? paddingClass : '', bodyClass]"
		>
			<slot name="body">
				<slot></slot>
			</slot>
		</div>

		<!-- Card Footer -->
		<div
			v-if="$slots.footer"
			class="card-footer px-6 py-4"
			:class="[
				footerClass,
				!noDivider && ($slots.default || $slots.body)
					? 'border-t border-border'
					: '',
			]"
		>
			<slot name="footer"></slot>
		</div>
	</div>
</template>

<script>
export default {
	name: 'CardComponent',
	props: {
		title: {
			type: String,
			default: '',
		},
		subtitle: {
			type: String,
			default: '',
		},
		icon: {
			type: String,
			default: '',
		},
		iconColor: {
			type: String,
			default: '',
		},
		iconBg: {
			type: String,
			default: 'bg-gray-100',
		},
		shadow: {
			type: String,
			default: 'sm',
			validator: (value) =>
				['none', 'sm', 'md', 'lg', 'xl'].includes(value),
		},
		hover: {
			type: Boolean,
			default: false,
		},
		clickable: {
			type: Boolean,
			default: false,
		},
		bordered: {
			type: Boolean,
			default: true,
		},
		padding: {
			type: String,
			default: 'md',
			validator: (value) =>
				['none', 'sm', 'md', 'lg', 'xl'].includes(value),
		},
		noDivider: {
			type: Boolean,
			default: false,
		},
		customClass: {
			type: String,
			default: '',
		},
		headerClass: {
			type: String,
			default: '',
		},
		bodyClass: {
			type: String,
			default: '',
		},
		footerClass: {
			type: String,
			default: '',
		},
	},
	computed: {
		shadowClass() {
			const shadows = {
				none: '',
				sm: 'shadow-sm',
				md: 'shadow-md',
				lg: 'shadow-lg',
				xl: 'shadow-xl',
			};
			return shadows[this.shadow];
		},
		hoverClass() {
			return this.hover ? 'hover:shadow-lg hover:-translate-y-0.5' : '';
		},
		paddingClass() {
			const paddings = {
				none: '',
				sm: 'p-3',
				md: 'p-6',
				lg: 'p-8',
				xl: 'p-10',
			};
			return paddings[this.padding];
		},
		iconBgClass() {
			return this.iconBg;
		},
	},
	methods: {
		handleClick(event) {
			if (this.clickable) {
				this.$emit('click', event);
			}
		},
	},
};
</script>

<style scoped>
.card-component {
	overflow: hidden;
}
</style>

<!--
USAGE EXAMPLES:

1. BASIC CARD:
<CardComponent title="Basic Card" subtitle="This is a subtitle">
  <p>Card content goes here</p>
</CardComponent>

2. CARD WITH ICON:
<CardComponent
  title="Vehicle Accident"
  subtitle="Claim #CLM-2024-001"
  icon="fa fa-car-crash"
  icon-color="#0A0D57"
  icon-bg="bg-blue-50"
>
  <p>Claim details...</p>
</CardComponent>

3. CARD WITH CUSTOM HEADER:
<CardComponent>
  <template #header>
    <div class="flex justify-between items-center">
      <h3>Custom Header</h3>
      <button class="text-primary">Edit</button>
    </div>
  </template>
  <p>Content here</p>
</CardComponent>

4. CARD WITH FOOTER:
<CardComponent title="Action Card">
  <template #body>
    <p>Some important information</p>
  </template>
  <template #footer>
    <div class="flex justify-end gap-2">
      <button class="px-4 py-2 text-text-secondary">Cancel</button>
      <button class="px-4 py-2 bg-primary text-white rounded">Confirm</button>
    </div>
  </template>
</CardComponent>

5. CLICKABLE CARD WITH HOVER:
<CardComponent
  title="Clickable Card"
  :hover="true"
  :clickable="true"
  @click="handleCardClick"
>
  <p>Click me!</p>
</CardComponent>

6. CARD WITH HEADER ACTIONS:
<CardComponent title="Settings">
  <template #header-actions>
    <button class="text-sm text-primary">
      <i class="fa fa-ellipsis-v"></i>
    </button>
  </template>
  <p>Settings content</p>
</CardComponent>

7. MINIMAL CARD (NO BORDER, NO SHADOW):
<CardComponent
  shadow="none"
  :bordered="false"
  padding="lg"
>
  <p>Minimal card design</p>
</CardComponent>

<script>
import CardComponent from './CardComponent.vue';

export default {
  components: {
    CardComponent
  },
  methods: {
    handleCardClick() {
      console.log('Card clicked!');
    }
  }
};
</script>
-->
